import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import LeftCard, { leftCardData } from "./LeftCard";
import WhatsMattersNowCard from "./WhatsMattersNowCard";
import WhatStillMattersCard from "./WhatStillMattersCard";
import WhatMattersToMeCard from "./WhatMattersToMeCard/WhatMattersToMeCard";
import CompositeIndex from "./CompositeIndex";
import "./previewSliderSectionLayout.scss";
import usePermissions from "../../../../../core/hooks/usePermissions";
/** Injected only while this section is mounted so 400–700 weights are not loaded for the whole app up front. */
const PREVIEW_SLIDER_MONTserrat_WEIGHTS_LINK_ID = "preview-slider-montserrat-wght-400-700";
const PREVIEW_SLIDER_MONTserrat_WEIGHTS_HREF = "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap";
const TAB_LABELS = [
    "What Matters Now",
    "What Matters to Me",
    "What Still Matters",
    "Composite Index",
];
const PREVIEW_SLIDER_ACTIVE_TAB_STORAGE_KEY = "previewSliderSectionActiveTab";
const COMPOSITE_INDEX_TAB_INDEX = 3;
function readStoredActiveTabIndex() {
    try {
        const raw = sessionStorage.getItem(PREVIEW_SLIDER_ACTIVE_TAB_STORAGE_KEY);
        if (raw == null)
            return 0;
        const n = Number.parseInt(raw, 10);
        if (!Number.isFinite(n) || n < 0 || n >= TAB_LABELS.length)
            return 0;
        return n;
    }
    catch (_a) {
        return 0;
    }
}
/**
 * Figma `1:4703` — vertical stack (header `1:4704` + two-column `1:4713`).
 * Layout-only shell; tab strip matches MCP typography and spacing.
 */
const PreviewSliderSection = () => {
    const [activeTab, setActiveTab] = useState(readStoredActiveTabIndex);
    const [splitIsHiding, setSplitIsHiding] = useState(false);
    const pendingTabRef = useRef(null);
    const { isGroupAdmin } = usePermissions();
    useLayoutEffect(() => {
        if (isGroupAdmin === false && activeTab === COMPOSITE_INDEX_TAB_INDEX) {
            setActiveTab(0);
        }
    }, [isGroupAdmin, activeTab]);
    useEffect(() => {
        let link = document.getElementById(PREVIEW_SLIDER_MONTserrat_WEIGHTS_LINK_ID);
        if (!link) {
            link = document.createElement("link");
            link.id = PREVIEW_SLIDER_MONTserrat_WEIGHTS_LINK_ID;
            link.rel = "stylesheet";
            link.href = PREVIEW_SLIDER_MONTserrat_WEIGHTS_HREF;
            document.head.appendChild(link);
        }
        return () => {
            link === null || link === void 0 ? void 0 : link.remove();
        };
    }, []);
    useEffect(() => {
        try {
            sessionStorage.setItem(PREVIEW_SLIDER_ACTIVE_TAB_STORAGE_KEY, String(activeTab));
        }
        catch (_a) {
            /* ignore quota / private mode */
        }
    }, [activeTab]);
    const handleTabClick = (index) => {
        if (index === activeTab)
            return;
        if (typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            setActiveTab(index);
            return;
        }
        pendingTabRef.current = index;
        setSplitIsHiding(true);
    };
    const handleSplitTransitionEnd = (event) => {
        if (event.target !== event.currentTarget)
            return;
        if (event.propertyName !== "opacity")
            return;
        if (!splitIsHiding || pendingTabRef.current === null)
            return;
        const next = pendingTabRef.current;
        pendingTabRef.current = null;
        setActiveTab(next);
        requestAnimationFrame(() => {
            setSplitIsHiding(false);
        });
    };
    return (React.createElement("div", { className: "preview-slider-frame", "data-node-id": "1:4703" },
        React.createElement("div", { className: "preview-slider-frame__header", role: "tablist", "aria-label": "Preview sections", "data-node-id": "1:4704" }, TAB_LABELS.map((label, index) => {
            if (index === COMPOSITE_INDEX_TAB_INDEX && isGroupAdmin === false) {
                return null;
            }
            const isActive = index === activeTab;
            return (React.createElement("button", { key: label, type: "button", role: "tab", "aria-selected": isActive, tabIndex: isActive ? 0 : -1, className: `preview-slider-frame__tab${isActive ? " preview-slider-frame__tab--active" : ""}`, "data-node-id": index === 0
                    ? "1:4705"
                    : index === 1
                        ? "1:4707"
                        : index === 2
                            ? "1:4709"
                            : "1:4711", onClick: () => handleTabClick(index) },
                React.createElement("span", { className: "preview-slider-frame__tab-inner" }, label)));
        })),
        React.createElement("div", { className: `preview-slider-frame__split${splitIsHiding ? " preview-slider-frame__split--is-hiding" : ""}`, "data-node-id": "1:4713", onTransitionEnd: handleSplitTransitionEnd },
            React.createElement("div", { className: "preview-slider-frame__split-left", "data-node-id": "1:4714" }, activeTab === COMPOSITE_INDEX_TAB_INDEX && isGroupAdmin !== false ? (React.createElement(LeftCard, { data: leftCardData.compositeIndex, activeTab: activeTab })) : (React.createElement(LeftCard, { data: activeTab === 1
                    ? leftCardData.whatMattersToMe
                    : activeTab === 2
                        ? leftCardData.whatStillMatters
                        : leftCardData.whatMattersNow, activeTab: activeTab }))),
            React.createElement("div", { className: "preview-slider-frame__split-right" }, activeTab === COMPOSITE_INDEX_TAB_INDEX && isGroupAdmin !== false ? (React.createElement(CompositeIndex, null)) : activeTab === 2 ? (React.createElement(WhatStillMattersCard, null)) : activeTab === 1 ? (React.createElement(WhatMattersToMeCard, null)) : (React.createElement(WhatsMattersNowCard, null))))));
};
export default PreviewSliderSection;
